import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar.tsx';
import PrivateRoute from '../PrivateRoute';
import TuneUpload from '../components/TuneUpload/TuneUpload.tsx';
import UploadHistory from '../components/UploadHistory/UploadHistory.tsx';
import NotFoundPage from '../components/common/NotFoundPage';
import AdminProtectedRoute from '../components/common/AdminProtectedRoute';
import LeaderboardProtectedRoute from '../components/common/LeaderboardProtectedRoute';
import UploadDefaults from '../components/UploadDefaults/UploadDefaults.tsx';
import Leaderboard from '../components/Leaderboard/Leaderboard.tsx';
import Subscriptions from '../components/Subscriptions/index';
import AdminPanel from '../components/Admin/AdminPanel.tsx';
import PlanDetails from '../components/Admin/PlanDetails.tsx';
import UserDetails from '../components/Admin/UserDetails.tsx';
import { FamilyPlanPage } from '../components/FamilyPlan';
import './AppRoutes.css';
import { useAuth } from '../components/auth/AuthContext';

interface AppRoutesProps {
  selectedTab: number;
  setSelectedTab: (tabIndex: number) => void;
  isAuthenticated: boolean;
  onLogin: (response: any) => void;
  GOOGLE_CLIENT_ID: string;
}

const AppRoutes: React.FC<AppRoutesProps> = ({ selectedTab, setSelectedTab, isAuthenticated, onLogin, GOOGLE_CLIENT_ID }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Sync tab state with route
  useEffect(() => {
    if (location.pathname === '/upload') {
      setSelectedTab(0);
    } else if (location.pathname === '/history') {
      setSelectedTab(1);
    }
  }, [location.pathname, setSelectedTab]);

  return (
    
    <Routes>
      {/* ✅ Redirect base URL to /upload */}
      <Route path="/" element={<Navigate to="/upload" replace />} />
      <Route
        path="/upload"
        element={
          <>
            <Navbar
              selectedTab={selectedTab}
              setSelectedTab={(tab) => {
                setSelectedTab(tab);
                if (tab === 0) navigate('/upload');
                if (tab === 1) navigate('/history');
              }}
              onLogout={handleLogout}
              isAuthenticated={isAuthenticated}
              onLogin={onLogin}
              GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
            />
            <div className="authenticated-content-container">
              <TuneUpload onLogin={onLogin} />
            </div>
          </>
        }
      />
      <Route
        path="/history"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <UploadHistory />
              </div>
            </>
          </PrivateRoute>
        }
      />
      <Route
        path="/upload-defaults"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <UploadDefaults />
              </div>
            </>
          </PrivateRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <LeaderboardProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <Leaderboard />
              </div>
            </LeaderboardProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <Subscriptions />
              </div>
            </>
          </PrivateRoute>
        }
      />
      <Route
        path="/subscriptions/monthly"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <Subscriptions />
              </div>
            </>
          </PrivateRoute>
        }
      />
      <Route
        path="/subscriptions/annual"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <Subscriptions />
              </div>
            </>
          </PrivateRoute>
        }
      />
      <Route
        path="/subscriptions/lifetime"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <Subscriptions />
              </div>
            </>
          </PrivateRoute>
        }
      />
      <Route
        path="/family-plan"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <FamilyPlanPage />
              </div>
            </>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <AdminPanel />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/subscription-plans"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <AdminPanel />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/user-management"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <AdminPanel />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/subscription-plans/:planName"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <PlanDetails />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/user-management/:userId"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  setSelectedTab(tab);
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <UserDetails />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/error-logs"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <AdminPanel />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/frontend-errors"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <AdminPanel />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/benchmarks"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <AdminPanel />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/tune-search"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <AdminProtectedRoute>
              <Navbar
                selectedTab={selectedTab}
                setSelectedTab={(tab) => {
                  if (tab === 0) navigate('/upload');
                  if (tab === 1) navigate('/history');
                }}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
                onLogin={onLogin}
                GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID}
              />
              <div className="authenticated-content-container">
                <AdminPanel />
              </div>
            </AdminProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;